/**
 * Non-PII query params VASS puts on its own URLs. Whitelist-only scrubbing
 * drops invitation PII (`uuid`, `veteranContactEmail`, `data`) and any other
 * unknown params (e.g. marketing tags on referrers).
 * `defaultPrivacyLevel: mask` only masks session-replay DOM; URL attributes
 * still need beforeSend scrubbing.
 *
 * - `cancel`: schedule vs cancel flow (`cancel=true`)
 * - `details`: confirmation details-only mode (`details=true`); never carries
 *   appointment content
 *
 * Appointment ids are 8-4-4-4-12 hex GUIDs in the path. Any id of that shape
 * is removed from logged URLs, not only ids on known routes.
 */
export const ALLOWED_QUERY_PARAMS = ['cancel', 'details'];

/**
 * Drop GUID-shaped ids anywhere in a URL or view name. A leading slash is
 * removed with the id so `/appointment/{id}/cancel` stays a single path.
 * VASS ids match this shape but are not strict RFC UUIDs: the version nibble
 * can fall outside 1-8, as in `f65a798e-b7b6-f111-aaad-001dd80d9e2a`.
 * @param {string} value
 * @returns {string}
 */
const scrubGuids = value =>
  value.replace(
    /\/?[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
    '',
  );

/**
 * Staging records every replay so a PII/PHI review does not depend on the
 * production sample. Every other environment uses `sessionReplaySampleRate`
 * from manifest.json.
 * @param {string} vspEnvironment
 * @param {number} manifestReplaySampleRate
 * @returns {number}
 */
export const sessionReplaySampleRate = (
  vspEnvironment,
  manifestReplaySampleRate,
) => (vspEnvironment === 'staging' ? 100 : manifestReplaySampleRate);

/**
 * Keep only allowlisted query params, and drop GUID-shaped ids.
 * Returns the original value when it is missing or not a parseable URL.
 * @param {string} urlString
 * @returns {string}
 */
export const scrubUrl = urlString => {
  if (!urlString || typeof urlString !== 'string') {
    return urlString;
  }

  try {
    const url = new URL(urlString, window.location.origin);
    const allowed = new URLSearchParams();
    ALLOWED_QUERY_PARAMS.forEach(param => {
      if (url.searchParams.has(param)) {
        allowed.set(param, url.searchParams.get(param));
      }
    });
    url.search = allowed.toString();
    // `URL` with a base always yields an absolute href (relative inputs get
    // window.location.origin prepended). RUM typically passes absolute
    // view.url / view.referrer / resource.url already.
    return scrubGuids(url.toString());
  } catch {
    return scrubGuids(urlString);
  }
};

/**
 * Datadog RUM `beforeSend` hook: scrub query strings and GUID-shaped ids on
 * view, resource, and error-resource URL attributes. A failed XHR keeps its
 * request URL on `error.resource.url` rather than `resource.url`. Mutates the
 * event in place and always returns true so the event is kept.
 * @param {Object} event - RUM event passed to beforeSend
 * @returns {boolean}
 */
export const datadogBeforeSend = event => {
  if (event?.view?.name) {
    // eslint-disable-next-line no-param-reassign
    event.view.name = scrubGuids(event.view.name);
  }
  if (event?.error?.resource?.url) {
    // eslint-disable-next-line no-param-reassign
    event.error.resource.url = scrubUrl(event.error.resource.url);
  }
  if (event?.view?.url) {
    // eslint-disable-next-line no-param-reassign
    event.view.url = scrubUrl(event.view.url);
  }
  if (event?.view?.referrer) {
    // eslint-disable-next-line no-param-reassign
    event.view.referrer = scrubUrl(event.view.referrer);
  }
  if (event?.resource?.url) {
    // eslint-disable-next-line no-param-reassign
    event.resource.url = scrubUrl(event.resource.url);
  }
  return true;
};
